/**
 * Settings store
 *
 * Manages user preferences, application settings, and configuration.
 *
 * Reference: Zustand store pattern
 * https://github.com/pmndrs/zustand
 */
import { create } from 'zustand';
const defaultSettings = {
    theme: 'system',
    language: 'en',
    notifications: {
        email: true,
        inApp: true,
        push: false,
        onMeetingComplete: true,
        onTranscriptionComplete: true,
        onSummaryComplete: true,
        onErrors: true,
    },
    audio: {
        quality: 'high',
        autoStart: false,
        noiseCancellation: true,
        speakerDetection: true,
    },
    transcription: {
        language: 'en',
        autoTranscribe: true,
        includeTimestamps: true,
        includeSpeakerLabels: true,
    },
    export: {
        defaultFormat: 'pdf',
        includeTranscript: true,
        includeSummary: true,
        includeActionItems: true,
        includeAudio: false,
    },
    privacy: {
        shareAnalytics: true,
        shareUsageData: false,
        retentionDays: 365,
        autoDelete: false,
    },
    lastUpdated: null,
};
export const useSettingsStore = create((set, get) => ({
    // State
    settings: null,
    isLoading: false,
    isSaving: false,
    error: null,
    // Actions
    setSettings: (settings) => {
        set({ settings });
    },
    updateSettings: (updates) => {
        set((state) => ({
            settings: state.settings
                ? { ...state.settings, ...updates, lastUpdated: new Date() }
                : { ...defaultSettings, ...updates, lastUpdated: new Date() },
        }));
    },
    updateNotificationSettings: (updates) => {
        set((state) => ({
            settings: state.settings
                ? {
                    ...state.settings,
                    notifications: {
                        ...state.settings.notifications,
                        ...updates,
                    },
                    lastUpdated: new Date(),
                }
                : {
                    ...defaultSettings,
                    notifications: {
                        ...defaultSettings.notifications,
                        ...updates,
                    },
                    lastUpdated: new Date(),
                },
        }));
    },
    updateAudioSettings: (updates) => {
        set((state) => ({
            settings: state.settings
                ? {
                    ...state.settings,
                    audio: {
                        ...state.settings.audio,
                        ...updates,
                    },
                    lastUpdated: new Date(),
                }
                : {
                    ...defaultSettings,
                    audio: {
                        ...defaultSettings.audio,
                        ...updates,
                    },
                    lastUpdated: new Date(),
                },
        }));
    },
    updateTranscriptionSettings: (updates) => {
        set((state) => ({
            settings: state.settings
                ? {
                    ...state.settings,
                    transcription: {
                        ...state.settings.transcription,
                        ...updates,
                    },
                    lastUpdated: new Date(),
                }
                : {
                    ...defaultSettings,
                    transcription: {
                        ...defaultSettings.transcription,
                        ...updates,
                    },
                    lastUpdated: new Date(),
                },
        }));
    },
    updateExportSettings: (updates) => {
        set((state) => ({
            settings: state.settings
                ? {
                    ...state.settings,
                    export: {
                        ...state.settings.export,
                        ...updates,
                    },
                    lastUpdated: new Date(),
                }
                : {
                    ...defaultSettings,
                    export: {
                        ...defaultSettings.export,
                        ...updates,
                    },
                    lastUpdated: new Date(),
                },
        }));
    },
    updatePrivacySettings: (updates) => {
        set((state) => ({
            settings: state.settings
                ? {
                    ...state.settings,
                    privacy: {
                        ...state.settings.privacy,
                        ...updates,
                    },
                    lastUpdated: new Date(),
                }
                : {
                    ...defaultSettings,
                    privacy: {
                        ...defaultSettings.privacy,
                        ...updates,
                    },
                    lastUpdated: new Date(),
                },
        }));
    },
    setLoading: (isLoading) => {
        set({ isLoading });
    },
    setSaving: (isSaving) => {
        set({ isSaving });
    },
    setError: (error) => {
        set({ error });
    },
    clearError: () => {
        set({ error: null });
    },
    reset: () => {
        set({
            settings: null,
            isLoading: false,
            isSaving: false,
            error: null,
        });
    },
}));
//# sourceMappingURL=settings.js.map